import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useAuth } from '../../auth/AuthProvider';
import { normalizeFiltersForKind } from './definition';
import { reportKindLabel } from './labels';
import type { ReportKind } from './types';

export interface ReportDrilldownMetric {
  readonly filters: Readonly<Record<string, string>>;
  readonly kind: ReportKind;
  readonly label: string;
  /** Null means the currently loaded detail contract has no exact value for this metric. */
  readonly value: ReactNode | null;
}

export function ReportDrilldowns({
  metrics,
}: {
  readonly metrics: readonly ReportDrilldownMetric[];
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const canViewReports =
    auth.status === 'authenticated' && auth.principal.permissions.includes('REPORT_VIEW');
  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <Typography component="h2" variant="h6">
        {t('reports.summaryTitle')}
      </Typography>
      <Stack component="dl" spacing={1.5} sx={{ m: 0, mt: 1.5 }}>
        {metrics.map(({ filters, kind, label, value }) => {
          const reportLabel = reportKindLabel(kind, t);
          return (
            <Box
              key={`${kind}-${label}`}
              sx={{
                alignItems: { sm: 'center' },
                display: 'grid',
                gap: 0.75,
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(10rem,1fr) minmax(10rem,1fr) auto' },
              }}
            >
              <Typography component="dt" color="text.secondary" variant="body2">
                {label}
              </Typography>
              <Typography component="dd" sx={{ m: 0 }} variant="body2">
                {value ?? t('reportSummaries.unavailable')}
              </Typography>
              {canViewReports ? (
                <Button
                  component={Link}
                  size="small"
                  to={reportDrilldownHref(kind, filters)}
                  variant="outlined"
                >
                  {t('reportSummaries.viewReport', { report: reportLabel })}
                </Button>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

export function reportDrilldownHref(
  kind: ReportKind,
  filters: Readonly<Record<string, string>>,
): string {
  const normalized = normalizeFiltersForKind(kind, filters);
  return `/reports/${kind}?${new URLSearchParams(
    Object.entries(normalized).map(([key, value]) => [key, String(value)]),
  ).toString()}`;
}
